import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Send, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { signInAnonymously } from 'firebase/auth'
import { auth } from '../services/firebase'
import { createLetter } from '../services/api'
import { logPageView, logLetterStarted, logLetterCreated, logLetterRejected, logLetterFailed, logButtonClick } from '../services/analytics'
import PepAppPremiumModal from '../components/PepAppPremiumModal'

const MAX_LENGTH = 1000
const MIN_LENGTH = 120

export default function WritePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [body, setBody] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showPepAppModal, setShowPepAppModal] = useState(false)
  const [hasStartedWriting, setHasStartedWriting] = useState(false)

  const charCount = body.length
  const isValidLength = charCount >= MIN_LENGTH && charCount <= MAX_LENGTH

  // Log page view
  useEffect(() => {
    logPageView('write')
  }, [])

  // Log when user starts writing
  useEffect(() => {
    if (body.length > 0 && !hasStartedWriting) {
      setHasStartedWriting(true)
      logLetterStarted()
    }
  }, [body, hasStartedWriting])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    logButtonClick('send_letter', 'write')

    if (!isValidLength) {
      setError(`Mektup ${MIN_LENGTH} ile ${MAX_LENGTH} karakter arasında olmalı.`)
      return
    }

    // Ensure user is authenticated
    let currentUser = user
    if (!currentUser) {
      try {
        const result = await signInAnonymously(auth)
        currentUser = result.user
      } catch {
        setError('Giriş yapılamadı. Lütfen tekrar deneyin.')
        logLetterFailed('auth_failed')
        return
      }
    }

    setIsSubmitting(true)

    try {
      const result = await createLetter(body.trim())
      
      if (result.success) {
        setSuccess(true)
        setShowPepAppModal(true)
        setBody('')
        logLetterCreated(result.letterId || 'unknown', charCount)
      } else if (result.rejectionReason) {
        setError(`Mektubunuz içerik kontrolünden geçemedi: ${result.rejectionReason}`)
        logLetterRejected(result.rejectionReason)
      } else {
        setError(result.error || 'Bir hata oluştu. Lütfen tekrar deneyin.')
        logLetterFailed(result.error || 'unknown_error')
      }
    } catch {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.')
      logLetterFailed('exception')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <PepAppPremiumModal open={showPepAppModal} onClose={() => setShowPepAppModal(false)} />
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-rose-100 text-center animate-fade-in">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="font-display text-2xl font-semibold text-ink mb-4">
            Mektubun Gönderildi! 💌
          </h2>
          <p className="font-body text-ink/70 mb-8">
            Mektubun havuza eklendi ve başka biri tarafından okunmayı bekliyor.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setSuccess(false)}
              className="btn-primary"
            >
              Başka Bir Mektup Yaz
            </button>
            <button
              onClick={() => navigate('/read')}
              className="btn-secondary"
            >
              Mektup Oku
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-ink/60 hover:text-ink transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="font-body text-sm">Ana Sayfa</span>
      </button>

      <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-rose-100 overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-6 py-4">
          <h1 className="font-display text-2xl font-semibold text-white">
            ✉️ Mektup Yaz
          </h1>
          <p className="text-rose-100 text-sm mt-1">
            Kalbindeki sözleri özgürce yaz
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Error message */}
          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6 animate-slide-up">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm font-body">{error}</p>
            </div>
          )}

          {/* Textarea */}
          <div className="letter-card p-0 mb-6">
            <div className="paper-texture rounded-lg">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Sevgili bilinmeyen dostum,&#10;&#10;Bugün seninle paylaşmak istediğim bir şey var..."
                className="letter-textarea"
                maxLength={MAX_LENGTH + 100}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Character count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-ink/60 font-body">
              {charCount < MIN_LENGTH && (
                <span className="text-amber-600">
                  En az {MIN_LENGTH - charCount} karakter daha yaz
                </span>
              )}
              {charCount >= MIN_LENGTH && charCount <= MAX_LENGTH && (
                <span className="text-green-600">
                  ✓ Mektubun gönderilmeye hazır
                </span>
              )}
              {charCount > MAX_LENGTH && (
                <span className="text-red-600">
                  {charCount - MAX_LENGTH} karakter fazla
                </span>
              )}
            </p>
            <span className={`text-sm font-mono ${
              charCount > MAX_LENGTH ? 'text-red-600' : 
              charCount >= MIN_LENGTH ? 'text-green-600' : 'text-ink/40'
            }`}>
              {charCount}/{MAX_LENGTH}
            </span>
          </div>

          {/* Info box */}
          <div className="bg-rose-50 border border-rose-100 rounded-lg p-4 mb-6">
            <p className="text-sm text-ink/70 font-body">
              <strong className="text-ink">📝 Not:</strong> Mektubun içerik kontrolünden geçecek. 
              Saygısız, uygunsuz veya rahatsız edici içerikler reddedilecektir.
            </p>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={!isValidLength || isSubmitting}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="spinner w-5 h-5 border-2" />
                <span>Gönderiliyor...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Mektubu Gönder</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}
